import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { Working } from "@/ui/Working";
import { usePlan } from "./use-plan";

/**
 * Pages a locked account can still reach: Today (its headline only, as a home
 * screen), the summary of the call that just used its minutes, its plan, and
 * the pages for managing or deleting its data.
 */
const OPEN_WHEN_LOCKED = new Set(["/today", "/talk/summary", "/you/plan", "/you/privacy", "/you/delete"]);

/**
 * Once the free minutes are spent and there is no plan, every other page
 * leads to the paywall. The server enforces the same wall on every paid
 * endpoint; this only keeps people from landing on a page that cannot load.
 */
export function PlanGate({ children }: { readonly children?: ReactNode }) {
  const plan = usePlan();
  const { pathname } = useLocation();

  if (plan.isPending) return <Working message="One moment…" />;
  if (plan.data?.locked && !OPEN_WHEN_LOCKED.has(pathname)) {
    return <Navigate to="/subscribe" replace />;
  }
  return <>{children ?? <Outlet />}</>;
}
